import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Medal, TextT } from "@phosphor-icons/react";
import { defaultMostProudOf, mostProudOfSchema } from "@stylingcv/schema";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, Input } from "@stylingcv/ui";
import { useForm } from "react-hook-form";
import type { z } from "zod";

import { SectionDialog } from "../sections/shared/section-dialog";

// Curated icons from StylingCVFont2018 for achievements/proud-of items
// Each class is verified to exist in apps/client/public/fonts/StylingCVFont2018/styles.css
export const mostProudOfIcons = [
  // Trophies & Medals
  { class: "trophy", name: "Trophy" },
  { class: "trophy3", name: "Trophy 3" },
  { class: "trophy4", name: "Trophy 4" },
  { class: "medal", name: "Medal" },
  { class: "medal-first", name: "First Place" },
  { class: "medal-second", name: "Second Place" },
  { class: "medal-third", name: "Third Place" },
  { class: "medal-star", name: "Medal Star" },
  { class: "certificate", name: "Certificate" },
  { class: "podium", name: "Podium" },

  // Stars & Ratings
  { class: "star-full", name: "Star" },
  { class: "star-half", name: "Star Half" },
  { class: "star-empty3", name: "Star Empty" },
  { class: "stars", name: "Stars" },
  { class: "starburst", name: "Starburst" },
  { class: "rating", name: "Rating" },
  { class: "rating2", name: "Rating 2" },
  { class: "rating3", name: "Rating 3" },

  // Business & Professional
  { class: "briefcase", name: "Briefcase" },
  { class: "briefcase3", name: "Briefcase 3" },
  { class: "portfolio", name: "Portfolio" },
  { class: "user-tie", name: "Business Person" },
  { class: "strategy", name: "Strategy" },
  { class: "presentation", name: "Presentation" },
  { class: "vcard", name: "Business Card" },
  { class: "store", name: "Store" },
  { class: "office", name: "Office" },

  // Charts & Growth
  { class: "stats-growth", name: "Growth" },
  { class: "stats-bars", name: "Stats Bars" },
  { class: "stats-bars3", name: "Stats Bars 3" },
  { class: "stats-bars4", name: "Stats Bars 4" },
  { class: "stats-dots", name: "Stats Dots" },
  { class: "bar-chart", name: "Bar Chart" },
  { class: "pie-chart", name: "Pie Chart" },
  { class: "graph", name: "Graph" },
  { class: "statistics", name: "Statistics" },
  { class: "equalizer", name: "Equalizer" },

  // Target & Goals
  { class: "target", name: "Target" },
  { class: "flag", name: "Flag" },
  { class: "flag3", name: "Flag 3" },
  { class: "finish", name: "Finish" },
  { class: "checkmark", name: "Checkmark" },
  { class: "checkmark-circle", name: "Checkmark Circle" },
  { class: "shield-check", name: "Shield Check" },
  { class: "task", name: "Task" },

  // Innovation & Ideas
  { class: "rocket", name: "Rocket" },
  { class: "magic-wand", name: "Magic Wand" },
  { class: "atom", name: "Atom" },
  { class: "lab", name: "Lab" },
  { class: "microscope", name: "Microscope" },
  { class: "brain", name: "Brain" },
  { class: "puzzle", name: "Puzzle" },
  { class: "puzzle2", name: "Puzzle 2" },

  // Education & Knowledge
  { class: "graduation", name: "Graduation" },
  { class: "graduation2", name: "Graduation 2" },
  { class: "graduation-cap", name: "Graduation Cap" },
  { class: "book", name: "Book" },
  { class: "book3", name: "Book 3" },
  { class: "books", name: "Books" },
  { class: "library", name: "Library" },
  { class: "reading", name: "Reading" },
  { class: "quill", name: "Quill" },
  { class: "pencil-ruler", name: "Pencil Ruler" },

  // People & Leadership
  { class: "users", name: "Team" },
  { class: "users4", name: "Team 4" },
  { class: "people", name: "People" },
  { class: "user", name: "User" },
  { class: "user-check", name: "User Check" },
  { class: "user-plus", name: "User Plus" },
  { class: "collaboration", name: "Collaboration" },
  { class: "man-woman", name: "Man Woman" },

  // Symbols & Values
  { class: "heart", name: "Heart" },
  { class: "heart5", name: "Heart 5" },
  { class: "diamond", name: "Diamond" },
  { class: "diamond3", name: "Diamond 3" },
  { class: "crown", name: "Crown" },
  { class: "shield", name: "Shield" },
  { class: "key", name: "Key" },
  { class: "gift", name: "Gift" },
  { class: "fire", name: "Fire" },

  // Global & Communication
  { class: "earth", name: "Earth" },
  { class: "sphere", name: "Globe" },
  { class: "sphere3", name: "Globe 3" },
  { class: "language", name: "Language" },
  { class: "megaphone", name: "Megaphone" },
  { class: "bubble", name: "Speech Bubble" },
  { class: "comments", name: "Comments" },

  // Technology & Engineering
  { class: "cog", name: "Cog" },
  { class: "cogs", name: "Cogs" },
  { class: "gear", name: "Gear" },
  { class: "code", name: "Code" },
  { class: "terminal", name: "Terminal" },
  { class: "chip", name: "Chip" },
  { class: "database", name: "Database" },
  { class: "server", name: "Server" },
  { class: "hammer-wrench", name: "Tools" },

  // Creative & Design
  { class: "camera", name: "Camera" },
  { class: "image", name: "Image" },
  { class: "film", name: "Film" },
  { class: "music", name: "Music" },
  { class: "piano", name: "Piano" },
  { class: "brush", name: "Brush" },
  { class: "paint-format", name: "Paint Format" },

  // Finance & Value
  { class: "coin-dollar", name: "Dollar" },
  { class: "coin-euro", name: "Euro" },
  { class: "coins", name: "Coins" },
  { class: "cash", name: "Cash" },
  { class: "piggy-bank", name: "Piggy Bank" },
  { class: "credit-card", name: "Credit Card" },

  // Misc Achievement-Relevant
  { class: "compass4", name: "Compass" },
  { class: "map", name: "Map" },
  { class: "road", name: "Road" },
  { class: "stairs-up", name: "Stairs Up" },
  { class: "ladder", name: "Ladder" },
  { class: "power", name: "Power" },
  { class: "infinite", name: "Infinite" },
  { class: "tree7", name: "Tree" },
  { class: "anchor", name: "Anchor" },
  { class: "lifebuoy", name: "Lifebuoy" },
  { class: "umbrella", name: "Umbrella" },
  { class: "law", name: "Law" },
  { class: "balance", name: "Balance" },
  { class: "construction", name: "Construction" },
];

const formSchema = mostProudOfSchema;
type FormValues = z.infer<typeof formSchema>;

export const MostProudOfDialog = () => {
  const form = useForm<FormValues>({
    defaultValues: { ...defaultMostProudOf, icon: "trophy" },
    resolver: zodResolver(formSchema),
  });

  const selectedIcon = form.watch("icon") || "trophy";

  const handleIconSelect = (iconClass: string) => {
    form.setValue("icon", iconClass, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <SectionDialog<FormValues> id="mostProudOf" form={form} defaultValues={defaultMostProudOf}>
      <div className="grid grid-cols-1 gap-6 p-2">
        {/* Header Input */}
        <FormField
          name="name"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Header`} *</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t`e.g., Physical Organization`} leadingIcon={<Medal size={15} />} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description Input */}
        <FormField
          name="description"
          control={form.control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t`Description`}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t`e.g., Creative Thinking, Effectiveness, Productivity`}
                  leadingIcon={<TextT size={15} />}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Icon Selection */}
        <FormField
          name="icon"
          control={form.control}
          render={() => (
            <FormItem>
              <FormLabel>{t`Select Icon`}</FormLabel>
              <FormControl>
                <div className="space-y-4">
                  {/* Selected Icon Preview */}
                  <div className="flex items-center gap-3 rounded-lg border bg-secondary/30 p-4">
                    <i className={`fa-${selectedIcon} text-2xl text-primary`}></i>
                    <span className="font-medium">{t`Selected Icon`}</span>
                  </div>

                  {/* Icon Grid */}
                  <div className="grid max-h-80 grid-cols-6 gap-2 overflow-y-auto bg-gray-50/50 p-3 dark:bg-gray-800/50">
                    {mostProudOfIcons.map((icon) => (
                      <button
                        key={icon.class}
                        type="button"
                        className={`flex flex-col items-center justify-center p-2 transition-all duration-200 hover:scale-105 hover:bg-secondary/50 hover:shadow-sm ${
                          selectedIcon === icon.class
                            ? "bg-primary/15 shadow-md"
                            : "bg-white dark:bg-gray-700"
                        } `}
                        title={icon.name}
                        onClick={() => {
                          handleIconSelect(icon.class);
                        }}
                      >
                        <i
                          className={`fa-${icon.class} mb-1 text-primary`}
                          style={{ fontSize: "32px" }}
                        ></i>
                      </button>
                    ))}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </SectionDialog>
  );
};
